import { acpAgentCatalogEntryForId } from '@crowdex/agent-contracts';
import { useEffect, useMemo, useState } from 'react';
import { useShellNavigation } from '@/components/sidebar/shell-navigation';
import { isDesktopPreviewPath } from '@/lib/assistants/directory';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { PREVIEW_AVATARS, PREVIEW_PERSONAS, PREVIEW_REPOSITORIES } from './workspace-preview-data';
import { fetchAvatars, fetchPersonas } from './workspace-resources-client';

/** A repository an assistant (project) can hold: a logical workspace, by its id. */
export type RepositoryOption = { id: string; name: string };

/** A saved agent an assistant may hand work to, and the agent it runs. */
export type PersonaOption = { id: string; name: string; agentName: string };

/** An avatar an assistant may speak as: a persona of the call plugin. */
export type AvatarOption = { id: string; name: string };

function usePreview(): boolean {
  return useShellNavigation().preview || isDesktopPreviewPath();
}

/** `repository:github.com/acme/project` → `acme/project`; a folder keeps its name. */
export function repositoryLabel(entry: { name: string; syncKey?: string | null }): string {
  const key = entry.syncKey ?? '';
  const match = /^repository:[^/]+\/(.+)$/.exec(key);
  return match?.[1] ?? entry.name;
}

/** The repositories the user has (their checkouts and Cloud workspaces), once each. */
export function useKnownRepositories(): RepositoryOption[] {
  const preview = usePreview();
  const synced = useDesktopShellStore((state) => state.syncedWorkspaces);
  return useMemo(() => {
    if (preview) return PREVIEW_REPOSITORIES;
    const byId = new Map<string, RepositoryOption>();
    for (const entry of synced)
      if (entry.logicalWorkspaceId !== undefined && !byId.has(entry.logicalWorkspaceId))
        byId.set(entry.logicalWorkspaceId, {
          id: entry.logicalWorkspaceId,
          name: repositoryLabel(entry),
        });
    return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name));
  }, [preview, synced]);
}

/** "Claude Code", "Codex"… for a saved agent's runtime id. */
export function agentDisplayName(agentId: string): string {
  return acpAgentCatalogEntryForId(agentId)?.name ?? agentId;
}

type Loaded<T> = { data: T; status: 'loading' | 'ready' | 'error'; error: string | null };

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}

/** The account's saved agents, as personas. */
export function usePersonas(): Loaded<PersonaOption[]> {
  const preview = usePreview();
  const [state, setState] = useState<Loaded<PersonaOption[]>>(() =>
    preview
      ? { data: PREVIEW_PERSONAS, status: 'ready', error: null }
      : { data: [], status: 'loading', error: null },
  );
  useEffect(() => {
    if (preview) return;
    let active = true;
    fetchPersonas().then(
      (profiles) =>
        active &&
        setState({
          data: profiles.map((profile) => ({
            id: profile.id,
            name: profile.name,
            agentName: agentDisplayName(profile.agentId),
          })),
          status: 'ready',
          error: null,
        }),
      (reason: unknown) =>
        active && setState({ data: [], status: 'error', error: message(reason) }),
    );
    return () => {
      active = false;
    };
  }, [preview]);
  return state;
}

/** The avatars (the call plugin's personas); none without a call plugin. */
export function useAvatars(): Loaded<AvatarOption[]> {
  const preview = usePreview();
  const [state, setState] = useState<Loaded<AvatarOption[]>>(() =>
    preview
      ? { data: PREVIEW_AVATARS, status: 'ready', error: null }
      : { data: [], status: 'loading', error: null },
  );
  useEffect(() => {
    if (preview) return;
    let active = true;
    fetchAvatars().then(
      (data) => active && setState({ data, status: 'ready', error: null }),
      (reason: unknown) =>
        active && setState({ data: [], status: 'error', error: message(reason) }),
    );
    return () => {
      active = false;
    };
  }, [preview]);
  return state;
}
